import { useDropzone } from 'react-dropzone';

import Box from '@mui/material/Box';
import { Stack, FormHelperText } from '@mui/material';

import { varAlpha } from 'src/theme/styles';

import { Iconify } from '../iconify';
import { uploadClasses } from './classes';
import { RejectionFiles } from './components/rejection-files';

import type { UploadProps } from './types';

// ----------------------------------------------------------------------

export function UploadBox({
  placeholder,
  error,
  disabled,
  className,
  helperText,
  onClick,
  sx,
  maxSize,
  ...other
}: UploadProps) {
  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    disabled,
    maxSize,
    ...other,
  });

  const hasError = isDragReject || error;

  return (
    <Stack>
      <Box
        {...getRootProps()}
        className={uploadClasses.uploadBox.concat(className ? ` ${className}` : '')}
        sx={{
          width: 64,
          height: 64,
          flexShrink: 0,
          display: 'flex',
          borderRadius: 1,
          cursor: 'pointer',
          alignItems: 'center',
          color: 'text.disabled',
          justifyContent: 'center',
          bgcolor: (theme) => varAlpha(theme.vars.palette.grey['500Channel'], 0.08),
          border: (theme) => `dashed 1px ${varAlpha(theme.vars.palette.grey['500Channel'], 0.16)}`,
          ...(isDragActive && { opacity: 0.72 }),
          ...(disabled && { opacity: 0.48, pointerEvents: 'none' }),
          ...(hasError && {
            color: 'error.main',
            borderColor: 'error.main',
            bgcolor: (theme) => varAlpha(theme.vars.palette.error.mainChannel, 0.08),
          }),
          '&:hover': { opacity: 0.72 },
          ...sx,
        }}
      >
        <input {...getInputProps()} onClick={onClick} />

        {placeholder || <Iconify icon="eva:cloud-upload-fill" width={28} />}
      </Box>
      {helperText && (
        <FormHelperText error={!!error} sx={{ px: 2 }}>
          {helperText}
        </FormHelperText>
      )}
      <RejectionFiles
        files={fileRejections.map(({ file, errors }) => ({
          file,
          errors: [
            { code: 'custom', message: `File size must be less than ${maxSize / (1024 * 1024)}MB` },
          ],
        }))}
      />
    </Stack>
  );
}
